import React from "react";
import { Link } from "react-router-dom";
import mx from "@/i18n/mx.json";
import { CheckCircle2, Mail, Phone, ShieldCheck } from "lucide-react";

// This page is Spanish-only per requirement
export default function ThankYou() {
  const data = mx.thankyou;
  const brand = mx.brand;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Minimal branded header (Spanish only, no language switcher) */}
      <header className="border-b border-slate-800 bg-[#0A2540] text-white" data-testid="thankyou-header">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
          <Link to="/" data-testid="thankyou-brand-link" className="flex items-center gap-2.5">
            <span className="flex h-9 w-9 items-center justify-center rounded-md bg-emerald-500/15 ring-1 ring-emerald-400/40">
              <ShieldCheck className="h-5 w-5 text-emerald-300" />
            </span>
            <span className="flex flex-col leading-tight">
              <span className="text-base font-bold tracking-tight">{brand.name}</span>
              <span className="text-[10px] uppercase tracking-[0.18em] text-slate-300">{brand.tagline}</span>
            </span>
          </Link>
          <span className="text-xs font-semibold uppercase tracking-wider text-emerald-300">{data.eyebrow}</span>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-4 py-16 sm:px-6 lg:px-8" data-testid="thankyou-page">
        <div className="rounded-2xl border border-slate-200 bg-white p-8 shadow-xl sm:p-10">
          <div className="flex items-center gap-3">
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100 text-emerald-700">
              <CheckCircle2 className="h-7 w-7" />
            </span>
            <span className="text-xs font-bold uppercase tracking-widest text-emerald-800">{data.eyebrow}</span>
          </div>
          <h1 className="mt-5 text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">{data.title}</h1>
          <p className="mt-4 text-base leading-relaxed text-slate-700">{data.subtitle}</p>

          <div className="mt-8 rounded-xl border border-slate-200 bg-slate-50 p-5">
            <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-700">{data.checklistTitle}</h2>
            <ul className="mt-3 space-y-2">
              {data.checklist.map((c, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 flex-none text-emerald-600" /> {c}
                </li>
              ))}
            </ul>
          </div>

          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Link
              to="/"
              data-testid="thank-you-back-home-button"
              className="inline-flex items-center gap-2 rounded-md bg-[#0A2540] px-5 py-3 text-sm font-semibold text-white hover:bg-[#0d2f52]"
            >
              {data.backHome}
            </Link>
            <a
              href={`mailto:${mx.footer.email}`}
              className="inline-flex items-center gap-2 rounded-md border border-slate-300 bg-white px-5 py-3 text-sm font-semibold text-slate-800 hover:bg-slate-50"
              data-testid="thankyou-email-link"
            >
              <Mail className="h-4 w-4" /> {mx.footer.email}
            </a>
            <a
              href={`tel:${mx.footer.phone}`}
              className="inline-flex items-center gap-2 rounded-md border border-slate-300 bg-white px-5 py-3 text-sm font-semibold text-slate-800 hover:bg-slate-50"
              data-testid="thankyou-phone-link"
            >
              <Phone className="h-4 w-4" /> {mx.footer.phone}
            </a>
          </div>

          <div className="mt-8 rounded-lg border border-emerald-200 bg-emerald-50 p-4">
            <h3 className="text-sm font-semibold text-emerald-900">{data.supportTitle}</h3>
            <p className="mt-1 text-sm text-emerald-800">{data.supportText}</p>
          </div>
        </div>
      </main>

      <footer className="border-t border-slate-200 bg-white">
        <div className="mx-auto max-w-7xl px-4 py-6 text-xs text-slate-500 sm:px-6 lg:px-8">
          {mx.footer.copyright}
        </div>
      </footer>
    </div>
  );
}
